import React, { useEffect, useMemo, useRef, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';

import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import SkipNextOutlinedIcon from '@material-ui/icons/SkipNextOutlined';

import VisualizadorCartao from './VisualizadorCartao';
import FormularioMarcacoes, { TOTAL_QUESTOES } from './FormularioMarcacoes';
import ContadorReserva from './ContadorReserva';
import DialogInvalidarProva from './DialogInvalidarProva';
import { CheckBox } from '../Inputs/CheckBox/CheckBox';
import { ButtonPrimary, ButtonDefault, ButtonWarning } from '../Button/Button';
import { useStyles } from './Style';

export const MODOS_SESSAO = {
    DIGITAR: 'DIGITAR',
    REVISAR: 'REVISAR',
};

const normalizarRespostas = respostas =>
    Array.from({ length: TOTAL_QUESTOES }, (_, index) => (respostas && respostas[index]) || '');

const segundosRestantes = reservadoAte => {
    if (!reservadoAte) return null;

    const alvo = new Date(reservadoAte).getTime();
    if (Number.isNaN(alvo)) return null;

    return Math.max(0, Math.round((alvo - Date.now()) / 1000));
};

const PainelSessaoDigitacao = ({
    prova,
    respostasIniciais,
    sugestoes,
    onRegistrar,
    onInvalidar,
    onPular,
    onReservaExpirar,
    onDirtyChange,
    loading,
}) => {
    const classes = useStyles();

    const id = prova ? prova.id : null;
    const chaveInicial = (respostasIniciais || []).join('|');
    const chaveSugestoes = (sugestoes || []).join('|');
    const reservadoAte = prova ? prova.reservadoAte : null;

    const confirmarRef = useRef(null);

    const [respostas, setRespostas] = useState(() => normalizarRespostas(respostasIniciais));
    const [valido, setValido] = useState(false);
    const [mostrarSugestoes, setMostrarSugestoes] = useState(Boolean(chaveSugestoes));
    const [dialogInvalidar, setDialogInvalidar] = useState(false);

    useEffect(() => {
        const iniciais = chaveInicial ? chaveInicial.split('|') : [];
        setRespostas(normalizarRespostas(iniciais));
        setValido(false);
        setMostrarSugestoes(Boolean(chaveSugestoes));
        setDialogInvalidar(false);
    }, [id, chaveInicial, chaveSugestoes]);

    const segundos = useMemo(() => segundosRestantes(reservadoAte), [reservadoAte]);

    const sujo = respostas.some(Boolean);
    useEffect(() => {
        if (onDirtyChange) onDirtyChange(sujo);
    }, [sujo, onDirtyChange]);

    const handleConfirmar = () => {
        if (!valido || !onRegistrar) return;

        onRegistrar(respostas);
    };

    const handleInvalidar = dados => {
        setDialogInvalidar(false);

        if (onInvalidar) onInvalidar(dados);
    };

    const focarConfirmar = () => {
        if (confirmarRef.current) confirmarRef.current.focus();
    };

    if (!prova) {
        return (
            <div className={classes.painelVazio}>
                <Typography variant="h6" color="textSecondary">Nenhuma prova reservada</Typography>
                <Typography variant="body2" color="textSecondary">Aguarde a próxima prova da fila.</Typography>
            </div>
        );
    }

    return (
        <div className={classes.painel}>
            {loading && <LinearProgress />}

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <VisualizadorCartao
                        urlImagemCorte={prova.urlImagemCorte}
                        urlImagemCartao={prova.urlImagemCartao}
                        height="60vh"
                        toolbarExtra={segundos !== null &&
                            <ContadorReserva
                                seconds={segundos}
                                onExpire={onReservaExpirar}
                                paused={Boolean(loading)}
                            />
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <div className={classes.formularioBox}>
                        {Boolean(chaveSugestoes) &&
                            <CheckBox
                                label="Mostrar respostas lidas anteriormente"
                                checked={mostrarSugestoes}
                                onChange={event => setMostrarSugestoes(event.target.checked)}
                            />
                        }

                        <FormularioMarcacoes
                            value={respostas}
                            onChange={setRespostas}
                            sugestoes={sugestoes}
                            showSugestoes={mostrarSugestoes}
                            disabled={Boolean(loading)}
                            onValidChange={setValido}
                            onTabUltimaQuestao={focarConfirmar}
                            onSubmit={handleConfirmar}
                        />
                    </div>
                </Grid>
            </Grid>

            <div className={classes.painelAcoes}>
                <ButtonWarning
                    name="Invalidar Prova"
                    startIcon={<BlockOutlinedIcon />}
                    onClick={() => setDialogInvalidar(true)}
                    disabled={Boolean(loading)}
                />
                <ButtonDefault
                    name="Pular"
                    startIcon={<SkipNextOutlinedIcon />}
                    onClick={onPular}
                    disabled={Boolean(loading)}
                    className={classes.painelAcao}
                />
                <ButtonPrimary
                    ref={confirmarRef}
                    name="Confirmar Marcações"
                    startIcon={loading ? <CircularProgress size={16} /> : <CheckOutlinedIcon />}
                    onClick={handleConfirmar}
                    disabled={!valido || Boolean(loading)}
                    className={classes.painelAcaoConfirmar}
                />
            </div>

            <DialogInvalidarProva
                open={dialogInvalidar}
                idProva={id}
                codigo={prova.codigo}
                loading={loading}
                onClose={() => setDialogInvalidar(false)}
                onConfirm={handleInvalidar}
            />
        </div>
    );
};

export default PainelSessaoDigitacao;
